/*
 * Times people pick for "later" (snooze and scheduled send), in the browser's own time zone, and the small menus
 * that offer them: a few plain choices, each with its time, and "Pick a date and time". Also the label menu, shared
 * by the selection toolbar and the reading pane.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { Field, Input } from "../ui/Field";
import { IconCheck, IconMinus } from "../ui/icons";
import { MenuItem, MenuLabel, MenuSeparator } from "../ui/Menu";
import type { Label } from "./types";

export type LaterChoice = { label: string; at: Date };

const at = (base: Date, days: number, hour: number) => new Date(base.getFullYear(), base.getMonth(), base.getDate() + days, hour, 0, 0, 0);
const short = (d: Date) => d.toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });

/** Snooze: later today (6 PM, while that's an hour off), tomorrow morning, and next week (Monday morning). */
export function snoozeChoices(now = new Date()): LaterChoice[] {
  const out: LaterChoice[] = [];
  const evening = at(now, 0, 18);
  if (evening.getTime() - now.getTime() >= 3600_000) out.push({ label: "Later today", at: evening });
  out.push({ label: "Tomorrow", at: at(now, 1, 8) });
  const toMonday = ((8 - now.getDay()) % 7) || 7;
  out.push({ label: "Next week", at: at(now, toMonday, 8) });
  return out;
}

/** Send later: tomorrow morning, and Monday morning (unless tomorrow is Monday). */
export function sendChoices(now = new Date()): LaterChoice[] {
  const out: LaterChoice[] = [{ label: "Tomorrow morning", at: at(now, 1, 8) }];
  const toMonday = ((8 - now.getDay()) % 7) || 7;
  if (toMonday !== 1) out.push({ label: "Monday morning", at: at(now, toMonday, 8) });
  return out;
}

/** The choices as menu items, each with its time, then "Pick a date and time…". */
export function LaterItems({ choices, onPick, onCustom }: { choices: LaterChoice[]; onPick: (iso: string) => void; onCustom: () => void }) {
  return (
    <>
      {choices.map((c) => (
        <MenuItem key={c.label} onSelect={() => onPick(c.at.toISOString())}>
          <span className="flex items-baseline justify-between gap-4">
            <span>{c.label}</span>
            <span className="text-[12px] text-dim tabular-nums">{short(c.at)}</span>
          </span>
        </MenuItem>
      ))}
      <MenuSeparator />
      <MenuItem onSelect={onCustom}>Pick a date and time…</MenuItem>
    </>
  );
}

/** A datetime-local value for a Date, in the browser's time zone. */
const local = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** "Pick a date and time": at least a minute from now, within a year (the API's own limits). */
export function PickTimeDialog({ open, title, description, action, onClose, onPick }: { open: boolean; title: string; description?: string; action: string; onClose: () => void; onPick: (iso: string) => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setValue(local(at(new Date(), 1, 8)));
      setError(null);
    }
  }, [open]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return setError("Choose a date and time.");
    if (d.getTime() < Date.now() + 60_000) return setError("Choose a time at least a minute from now.");
    if (d.getTime() > Date.now() + 365 * 86_400_000) return setError("Choose a time within a year.");
    onPick(d.toISOString());
  };
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, " ");
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      size="sm"
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="pick-time" variant="primary">
            {action}
          </Button>
        </>
      }
    >
      <form id="pick-time" onSubmit={submit} noValidate>
        <Field label="Date and time" hint={`In your time zone, ${zone}.`} error={error}>
          <Input type="datetime-local" value={value} min={local(new Date(Date.now() + 120_000))} onChange={(e) => setValue(e.target.value)} />
        </Field>
      </form>
    </Dialog>
  );
}

/** Labels as menu items: a check when every chosen conversation has it, a dash when some do. Making labels is in Settings. */
export function LabelItems({ labels, has, onToggle, onManage }: { labels: Label[]; has: (id: string) => "all" | "some" | "none"; onToggle: (id: string, on: boolean) => void; onManage: () => void }) {
  return (
    <>
      <MenuLabel>Labels</MenuLabel>
      {labels.length ? (
        labels.map((l) => {
          const state = has(l.id);
          return (
            <MenuItem key={l.id} icon={state === "all" ? <IconCheck size={15} /> : state === "some" ? <IconMinus size={15} /> : <span className="inline-block w-[15px]" />} onSelect={() => onToggle(l.id, state !== "all")}>
              {l.name}
            </MenuItem>
          );
        })
      ) : (
        <p className="px-2.5 py-1.5 text-[12.5px] text-dim">No labels yet.</p>
      )}
      <MenuSeparator />
      <MenuItem onSelect={onManage}>{labels.length ? "Manage labels…" : "Make a label…"}</MenuItem>
    </>
  );
}
